import React, { createContext, useContext, useState, useCallback } from "react";
import * as api from "../api/index";

const OrderSessionContext = createContext();

export function useOrderSession() {
  const ctx = useContext(OrderSessionContext);
  return ctx || {
    activeSessionId: null,
    activeSession: null,
    openSessions: [],
    loading: false,
    fetchOpenSessions: () => Promise.resolve([]),
    createSession: () => Promise.reject(new Error("No provider")),
    loadSession: () => Promise.reject(new Error("No provider")),
    clearActiveSession: () => {},
    addItemToSession: () => Promise.reject(new Error("No provider")),
    removeItemFromSession: () => Promise.reject(new Error("No provider")),
    updateItemInSession: () => Promise.reject(new Error("No provider")),
    checkoutSession: () => Promise.reject(new Error("No provider")),
    setActiveSession: () => {},
    refreshActiveSession: () => Promise.resolve(),
  };
}

// Convert session item (with populated itemId) to cart item format
export function sessionItemToCartItem(sessionItem) {
  const item = sessionItem.itemId || sessionItem;
  if (!item) return null;
  const basePrice =
    sessionItem.originalRate ??
    item.units?.sale?.price ??
    sessionItem.rate ??
    item.rate ??
    0;
  const effectivePrice =
    sessionItem.rate ?? item.units?.sale?.price ?? sessionItem.originalRate ?? 0;
  const priceAdjusted =
    sessionItem.priceAdjusted ??
    Math.abs(parseFloat(effectivePrice) - parseFloat(basePrice)) > 0.0001;
  return {
    ...item,
    _id: item._id,
    itemName: item.itemName || item.name,
    quantity: sessionItem.qty ?? item.quantity ?? 1,
    units: item.units || { sale: { price: sessionItem.rate, unit: {} } },
    originalPrice: parseFloat(basePrice) || 0,
    overridePrice: priceAdjusted ? parseFloat(effectivePrice) : null,
    priceAdjusted: priceAdjusted,
    remark: sessionItem.remark ?? null,
  };
}

// Merge session items by itemId for cart summary (one cart item per product, total qty)
export function getCartItemsFromSession(session) {
  if (!session?.items?.length) return [];
  const byId = {};
  session.items.forEach((si) => {
    const id = si.itemId?._id?.toString() || si.itemId?.toString();
    if (!id) return;
    if (!byId[id]) {
      const item = si.itemId || {};
      byId[id] = {
        _id: item._id || si.itemId,
        itemName: si.name || item.itemName || item.name,
        quantity: 0,
        units: item.units || { sale: { price: si.rate, unit: {} } },
        originalPrice:
          si.originalRate ??
          item.units?.sale?.price ??
          si.rate ??
          item.rate ??
          0,
        overridePrice:
          si.priceAdjusted || Math.abs((si.rate || 0) - (si.originalRate ?? si.rate ?? 0)) > 0.0001
            ? si.rate
            : null,
        priceAdjusted:
          si.priceAdjusted ??
          Math.abs((si.rate || 0) - (si.originalRate ?? si.rate ?? 0)) > 0.0001,
        remark: si.remark ?? null,
        ...item,
      };
    }
    byId[id].quantity += si.qty || 0;
  });
  return Object.values(byId);
}

export function OrderSessionProvider({ children }) {
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [openSessions, setOpenSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchOpenSessions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.getOrderSessions({ status: "OPEN" });
      setOpenSessions(data || []);
      return data || [];
    } catch (err) {
      console.error("fetchOpenSessions error:", err);
      setOpenSessions([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createSession = useCallback(async (label) => {
    setLoading(true);
    try {
      const { data } = await api.createOrderSession({ label });
      setActiveSessionId(data._id);
      setActiveSession(data);
      setOpenSessions((prev) => [data, ...prev]);
      return data;
    } catch (err) {
      console.error("createSession error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSession = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.getOrderSession(id);
      setActiveSessionId(id);
      setActiveSession(data);
      return data;
    } catch (err) {
      console.error("loadSession error:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshActiveSession = useCallback(async () => {
    if (!activeSessionId) return null;
    try {
      const { data } = await api.getOrderSession(activeSessionId);
      setActiveSession(data);
      return data;
    } catch (err) {
      console.error("refreshActiveSession error:", err);
      return null;
    }
  }, [activeSessionId]);

  const clearActiveSession = useCallback(() => {
    setActiveSessionId(null);
    setActiveSession(null);
  }, []);

  const addItemToSession = useCallback(async (sessionId, item) => {
    const baseRate = parseFloat(
      item?.originalPrice ?? item?.units?.sale?.price ?? item?.rate ?? 0
    );
    const overrideRate =
      item?.overridePrice != null && item.overridePrice !== ""
        ? parseFloat(item.overridePrice)
        : null;
    const effectiveRate =
      overrideRate != null && !Number.isNaN(overrideRate) && overrideRate > 0
        ? overrideRate
        : parseFloat(item?.units?.sale?.price ?? item?.rate ?? baseRate ?? 0);
    const taxPercent = parseFloat(item?.vat ?? 0);
    const priceAdjusted =
      overrideRate != null &&
      !Number.isNaN(overrideRate) &&
      Math.abs(overrideRate - baseRate) > 0.0001;
    const { data } = await api.addItemToOrderSession(sessionId, {
      itemId: item._id,
      name: item.itemName || item.name,
      rate: effectiveRate,
      originalRate: baseRate,
      priceAdjusted,
      qty: item.quantity ?? 1,
      taxPercent,
      remark: item.remark ?? null,
    });
    setActiveSession(data);
    return data;
  }, []);

  const removeItemFromSession = useCallback(async (sessionId, itemIdOrPayload) => {
    const { data } = await api.removeItemFromOrderSession(sessionId, itemIdOrPayload);
    setActiveSession(data);
    return data;
  }, []);

  const updateItemInSession = useCallback(async (sessionId, itemIdOrPayload, qtyOrUndefined) => {
    const body =
      typeof itemIdOrPayload === "object" && itemIdOrPayload !== null
        ? itemIdOrPayload
        : { itemId: itemIdOrPayload, qty: qtyOrUndefined };
    const { data } = await api.updateItemInOrderSession(sessionId, body);
    setActiveSession(data);
    return data;
  }, []);

  const checkoutSession = useCallback(async (sessionId, paymentData) => {
    const { data } = await api.checkoutOrderSession(sessionId, paymentData);
    setActiveSessionId(null);
    setActiveSession(null);
    await fetchOpenSessions();
    return data;
  }, [fetchOpenSessions]);

  return (
    <OrderSessionContext.Provider
      value={{
        activeSessionId,
        activeSession,
        openSessions,
        loading,
        fetchOpenSessions,
        createSession,
        loadSession,
        clearActiveSession,
        addItemToSession,
        removeItemFromSession,
        updateItemInSession,
        checkoutSession,
        setActiveSession,
        refreshActiveSession,
      }}
    >
      {children}
    </OrderSessionContext.Provider>
  );
}
